import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Stack, Typography, styled } from '@mui/material';
import { sendPhoneVerificationCode } from 'api';
import { Button } from 'components/Ui-V3/Button';
import { PhoneField, SelectField, TextField } from 'components/Ui-V3/InputFields';
import { ROUTES } from 'constants/routes';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { TEAM_ADMIN, useUserContext } from 'stores/user';
import { EColors, getColor } from 'theme';
import { signOut } from 'utils/signOut';
import { z } from 'zod';

const PHONE_ERRORS = ['The phone has already been taken.', 'Please provide a valid phone number.'];

export const Title = styled(Typography)({
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: '1.125em',
    color: getColor(EColors.pureBlack),
    fontFamily: '"Neue Montreal", Roboto, Inter, -apple-system, BlinkMacSystemFont, sans-serif',
});

export const Description = styled(Typography)(({ theme }) => ({
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: '1.125em',
    color: getColor(EColors.v3TextSecondary),
    marginTop: theme.spacing(1),
    fontFamily: '"General Sans", "SF Pro Text", -apple-system, BlinkMacSystemFont, Roboto, sans-serif',
}));

export const Divider = styled(Box)(({ theme }) => ({
    height: 1,
    width: '100%',
    backgroundColor: getColor(EColors.v3Border),
    margin: theme.spacing(3, 0),
}));

const Label = styled(Typography)(({ theme }) => ({
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: '1.125em',
    color: getColor(EColors.v3TextPrimary),
    marginTop: theme.spacing(1),
    fontFamily: '"Neue Montreal", Roboto, Inter, -apple-system, BlinkMacSystemFont, sans-serif',
}));

const LabelSubText = styled(Typography)(({ theme }) => ({
    fontSize: '.875rem',
    fontWeight: 500,
    lineHeight: '1.125em',
    color: getColor(EColors.v3TextSecondary),
    marginTop: theme.spacing(1),
    fontFamily: '"General Sans", "SF Pro Text", -apple-system, BlinkMacSystemFont, Roboto, sans-serif',
}));

const ProfileSchema = z.object({
    firstName: z.string().trim().min(1, 'This field is required'),
    lastName: z.string().trim().min(1, 'This field is required'),
    phone: z.string().trim().min(1, 'This field is required'),
    companyName: z.string().trim().min(1, 'This field is required'),
    companySize: z.string().min(1, 'This field is required'),
});

export type ProfileFormData = z.infer<typeof ProfileSchema>;

// Company Name is auto-seeded from the user's email domain (e.g. "gmail.com").
// Reject that value so new users must enter a real company name.
// Company fields are only persisted for team admins (see VerifyPhone), so for
// non-admins they are read-only and exempt from validation.
const buildProfileSchema = (emailDomain: string, isTeamAdmin: boolean) =>
    ProfileSchema.extend({
        companyName: isTeamAdmin
            ? ProfileSchema.shape.companyName.refine(
                  value => !emailDomain || value.toLowerCase() !== emailDomain.toLowerCase(),
                  'Please enter your company name'
              )
            : z.string(),
        companySize: isTeamAdmin ? ProfileSchema.shape.companySize : z.string(),
    });

const getEmailDomain = (email?: string) => email?.split('@')[1]?.trim() ?? '';

export function ProfileDataSave({ onContinue }: { onContinue: (data: ProfileFormData) => void }) {
    const { user, clearUser } = useUserContext();
    const [submitting, setSubmitting] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const emailDomain = getEmailDomain(user.email);
    const isTeamAdmin = user.team?.role === TEAM_ADMIN;
    const resolver = useMemo(
        () => zodResolver(buildProfileSchema(emailDomain, isTeamAdmin)),
        [emailDomain, isTeamAdmin]
    );

    // A first-time admin's team is seeded server-side with their email domain, so start them blank
    // and make them type a real company name. An invited member can't rename the team, so show them
    // the name it already has.
    const seededCompanyName = isTeamAdmin ? '' : (user.team?.name ?? '');

    const {
        control,
        formState: { errors, isValid },
        handleSubmit,
        setError,
    } = useForm<ProfileFormData>({
        resolver,
        defaultValues: {
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            phone: user.phone || '',
            companyName: seededCompanyName,
            companySize: user.team?.companySize || '',
        },
    });

    const onSubmit = async (data: ProfileFormData) => {
        try {
            setSubmitting(true);

            await sendPhoneVerificationCode({ phone: data.phone });

            onContinue(data);
        } catch (error) {
            const errorMsg =
                (error as Error)?.message ||
                (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                String(error);

            // Check for specific error and set form error
            if ((errorMsg && errorMsg.includes(PHONE_ERRORS[0])) || errorMsg.includes(PHONE_ERRORS[1])) {
                setError('phone', { type: 'manual', message: errorMsg });
            } else {
                enqueueSnackbar(errorMsg, { variant: 'error' });
            }
        } finally {
            setSubmitting(false);
        }
    };

    async function handleSignOut() {
        await signOut(user?.id, clearUser);
        window.location.href = ROUTES.SIGN_IN;
    }

    return (
        <>
            <Title>Let's set up your account</Title>
            <Description>Enter the following details to set your profile.</Description>
            <Divider sx={{ marginTop: '32px', marginBottom: '24px' }} />
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Stack spacing={3} sx={{ paddingBottom: 7 }}>
                    <Label>We need a few details to get you event ready</Label>
                    <Stack spacing={2}>
                        <Stack spacing={2} direction="row">
                            <Box flex={1}>
                                <Controller
                                    name="firstName"
                                    control={control}
                                    render={({ field }) => {
                                        return (
                                            <TextField
                                                {...field}
                                                error={errors.firstName}
                                                label="First Name"
                                                placeholder="Enter first name"
                                            />
                                        );
                                    }}
                                />
                            </Box>
                            <Box flex={1}>
                                <Controller
                                    name="lastName"
                                    control={control}
                                    render={({ field }) => {
                                        return (
                                            <TextField
                                                {...field}
                                                error={errors.lastName}
                                                label="Last Name"
                                                placeholder="Enter last name"
                                            />
                                        );
                                    }}
                                />
                            </Box>
                        </Stack>
                        <Controller
                            name="phone"
                            control={control}
                            render={({ field }) => (
                                <PhoneField
                                    {...field}
                                    label="Phone Number"
                                    error={errors.phone}
                                    value={field.value || ''}
                                    onChange={value => {
                                        const formattedValue = value.startsWith('+') ? value : `+${value}`;
                                        field.onChange(formattedValue);
                                    }}
                                />
                            )}
                        />
                    </Stack>
                    <Divider />
                    <Stack spacing={1}>
                        <Label>Company details</Label>
                        <LabelSubText>This will help us get your account set for you.</LabelSubText>
                    </Stack>
                    <Controller
                        name="companyName"
                        control={control}
                        render={({ field }) => {
                            return (
                                <TextField
                                    {...field}
                                    error={errors.companyName}
                                    label="Company Name"
                                    placeholder="Enter company name"
                                    disabled={!isTeamAdmin}
                                />
                            );
                        }}
                    />
                    <Divider />
                    <Label>How many team members does your company have?</Label>
                    <Controller
                        name="companySize"
                        control={control}
                        render={({ field }) => (
                            <SelectField
                                {...field}
                                error={errors.companySize}
                                label="Company Size"
                                disabled={!isTeamAdmin}
                                options={[
                                    { label: '1-10', value: '1-10' },
                                    { label: '11-50', value: '11-50' },
                                    { label: '51-100', value: '51-100' },
                                    { label: '101-1,000', value: '101-1,000' },
                                    { label: '1,001+', value: '1,001+' },
                                ]}
                            />
                        )}
                    />
                    <Box display="flex" gap={2} sx={{ marginTop: 5 }}>
                        <Button variant="outline" fullWidth onClick={handleSignOut}>
                            Sign Out
                        </Button>
                        <Button variant="primary" fullWidth type="submit" disabled={!isValid} loading={submitting}>
                            Next
                        </Button>
                    </Box>
                </Stack>
            </form>
        </>
    );
}
